import classNames from 'classnames'
import React from 'react'

import {ButtonProps, getBaseButtonClassNames, LoadingButton} from './Button'

export interface LinkButtonProps extends ButtonProps {
  openNewTab?: boolean
  url: string
  width?: 'auto' | 'full' | 'responsive'
  style?: React.CSSProperties
}

/**
 * Utility method to get classes for link buttons based on props
 * @param disabled Whether the button is disabled
 * @param shape One of the shapes defined in the interface.
 * @param size One of the sizes defined in the interface.
 * @param variant One of the variant strings defined in the interface.
 * @param width One of the width strings defined in the interface.
 * @returns a set of classes that match the props.
 */
export function getBaseLinkButtonClassNames(
  disabled: LinkButtonProps['disabled'],
  shape: LinkButtonProps['shape'],
  size: LinkButtonProps['size'],
  width: LinkButtonProps['width'],
  variant?: LinkButtonProps['variant'],
): string {
  return classNames(
    getBaseButtonClassNames(disabled, shape, size, variant),
    disabled && 'pointer-events-none',
    'justify-center inline-flex items-center text-center',
    {'w-auto': width === 'auto', 'w-full': width === 'full', 'w-full md:w-auto': width === 'responsive'},
  )
}

/**
 * A button component for external links.
 * @param props.children Elements to render inside of the button outline.
 * @param props.className Classes to adjust the button's styling. Existing styles cannot be over-ridden.
 * @param props.disabled State of button.
 * @param props.onButtonClick Callback the button will call when clicked.
 * @param props.openNewTab Opens a new tab when the button is clicked (for external links only). Defaults to false.
 * @param props.shape Whether the button is rounded or square.
 * @param props.size Button size.
 * @param props.url URL the button will link to.
 * @param props.variant Defined button variants that match our style guide.
 * @param props.width Defined width variants that match our style guide.
 * @returns A LinkButton
 */
export const LinkButton: React.FC<LinkButtonProps & {style?: React.CSSProperties}> = ({
  children,
  className,
  disabled = false,
  loading = false,
  onButtonClick,
  openNewTab = false,
  shape = 'rounded',
  size,
  url,
  variant,
  width = 'auto',
  style,
}: LinkButtonProps) => (
  <a
    className={classNames(getBaseLinkButtonClassNames(disabled, shape, size, width, variant), className)}
    href={url}
    onClick={onButtonClick}
    rel="noreferrer noopener"
    style={style}
    target={openNewTab ? '_blank' : '_self'}
  >
    {!loading && children}
    {loading && <LoadingButton />}
    {openNewTab && <span className="sr-only"> (opens in a new window)</span>}
  </a>
)

export default LinkButton
